/**
 * Provides functionality to construct component instances from DOM elements and manage component initialization.
 * @module component_constructor
 */

import {RankersNamespaceKey, ensureRankersNamespace} from "../constants/rankers_namespace";

/**
 * Name of the property used to mark elements as "componentized".
 * When an element is initialized with a Component then we add the componentTypeKey to the element's "componentizedWith" array.
 * The 'ComponentConstructor.isElementComponentized` method can check this property to see if an element is already inited with any specific type of component.
 */
export const ComponentizedPropertyName = 'componentizedWith';


/**
 * Class responsible for constructing and initializing components.
 * ComponentConstructor can find elements that are not yet initialized with a component.
 * This class can search multiple root elements and return an array of instances of the specified component class.
 * The class will  mark the elements as initialized so that they are not initialized again.
 */
class ComponentConstructor {

    /**
     * Checks if an element is already initialized with a specific component type.
     * @param {Element} element - The DOM element to check.
     * @param {string} componentTypeKey - The key representing the component type.
     * @returns {boolean} True if the element is initialized, false otherwise.
     */
    static isElementComponentized(element, componentTypeKey) {
        if (!element.hasOwnProperty(ComponentizedPropertyName)) return false;
        if (!Array.isArray(element[ComponentizedPropertyName])) return false;
        return element[ComponentizedPropertyName].includes(componentTypeKey);
    }

    /**
     * Marks an element as initialized with a specific component type.
     * @param {Element} element - The DOM element to mark.
     * @param {string} componentTypeKey - The key representing the component type.
     */
    static markElementAsComponentized(element, componentTypeKey) {
        if (!element) return;
        if (!componentTypeKey) return;
        if (element.hasOwnProperty(ComponentizedPropertyName)) {
            if (!element[ComponentizedPropertyName].includes(componentTypeKey)) element[ComponentizedPropertyName].push(componentTypeKey);
        } else {
            element[ComponentizedPropertyName] = [componentTypeKey];
        }
    }

    constructor() {
        this.ID = Math.random().toString(36).substr(2, 3);
        this.rootNodes = [document.documentElement];
        this.componentInitializers = [];
    }

    /**
     * Gets the current root nodes for component construction.
     * @returns {Array} Array of root nodes.
     */
    getRootNodes() {
        return this.rootNodes;
    }

    /**
     * Adds a new root node for component construction.
     * Then we immediately observe the new root node for any elements that need to be "componentized".
     * @param {Element} node - The new root node to add.
     */
    addRootNode(node) {
        this.rootNodes.push(node);
        this.observeElementsForComponents();
    }

    /**
     * Removes a root node from component construction.
     * @param {Element} node - The root node to remove.
     */
    removeRootNode(node) {
        this.rootNodes = this.rootNodes.filter(rootNode => rootNode !== node);
    }

    observeElementsForComponents() {
        this.componentInitializers.forEach(componentInitializer => {
            componentInitializer.observeElements();
        });
    }

    /**
     * Constructs component instances for each uninitialized element matching the selector.
     * @returns {Array} Array of constructed component instances.
     * @param {ComponentInitializer} componentInitializer - contains the component class, selector, and other configuration.
     */
    construct(componentInitializer) {
        if (!this.componentInitializers.includes(componentInitializer)) this.componentInitializers.push(componentInitializer);
        const klass = componentInitializer.klass;
        return this.uninitializedElements(componentInitializer.selector, componentInitializer.componentTypeKey).map(el => {
            const klassInstance = new klass(el);
            ComponentConstructor.markElementAsComponentized(el, componentInitializer.componentTypeKey);
            return klassInstance;
        });
    }

    /**
     * Finds all uninitialized elements within the root nodes.
     * @param {string} selector - The CSS selector to match elements.
     * @param {string} componentTypeKey - The component type key.
     * @returns {Array} Array of uninitialized elements.
     */
    uninitializedElements(selector, componentTypeKey) {
        const elements = [];
        this.rootNodes.forEach((rootNode) => {
            if (rootNode.querySelector(selector) === null) return [];  // querySelector() is faster - return quickly if nothing matching
            Array.from(rootNode.querySelectorAll(selector)).forEach((element) => {
                if (!ComponentConstructor.isElementComponentized(element, componentTypeKey)) {
                  elements.push(element);
                }
            })
        });
        return elements;
    }

}

/**
 * Key for the singleton instance of ComponentConstructor on the window object.
 * @type {string}
 */
const RankersComponentConstructorKey = 'ComponentConstructor';

/**
 * We ensure there's a singleton instance of ComponentConstructor because there should only be one instance we ever reference.
 * Because separate bundled JS files need to share the same instance of ComponentConstructor, we will define it on the window object.
 **/
function ensureComponentConstructorInstance() {
    ensureRankersNamespace();
    if (!window[RankersNamespaceKey][RankersComponentConstructorKey]) window[RankersNamespaceKey][RankersComponentConstructorKey] = new ComponentConstructor();
    return window[RankersNamespaceKey][RankersComponentConstructorKey];
}

ensureComponentConstructorInstance();

/**
 * Gets the singleton instance of ComponentConstructor.
 * @returns {ComponentConstructor} The singleton instance of ComponentConstructor.
 */
function getSingletonInstance() {
    return ensureComponentConstructorInstance();
}

/**
 * Constructs components using the singleton instance of ComponentConstructor.
 * It references the singleton instance of ComponentConstructor which holds the root nodes we'll search for elements.
 * @returns {Array} Array of constructed component instances.
 * @param componentInitializer
 */
export const constructComponents = (componentInitializer) => {
    return getSingletonInstance().construct(componentInitializer);
}

/**
 * Adds a root node using the singleton instance of ComponentConstructor.
 * @param {Element} node - The root node to add.
 */
export const addComponentRootNode = (node) => {
    getSingletonInstance().addRootNode(node);
}

/**
 * Removes a root node using the singleton instance of ComponentConstructor.
 * @param {Element} node - The root node to remove.
 */
export const removeComponentRootNode = (node) => {
    getSingletonInstance().removeRootNode(node);
}

/**
 * Gets root nodes using the singleton instance of ComponentConstructor.
 * @returns {Array} Array of current root nodes.
 */
export const getComponentRootNodes = () => {
    return getSingletonInstance().getRootNodes();
}